import React, { useState, useRef } from 'react';
import { Home, BookOpen, Wand2, Sparkles, MessageSquare, Users, Clock, FlaskConical, ChevronLeft, ChevronRight, Lock, Palette } from 'lucide-react';
import { StorageWidget } from './StorageWidget';
import { ProfileDropdown } from './ProfileDropdown';
type SidebarProps = {
  activePage: string;
  onNavigate: (page: string) => void;
  onOpenSearch?: () => void;
  onViewRecentItem?: (id: number, type: 'story' | 'storybook') => void;
  isMinimized: boolean;
  onToggleMinimize: () => void;
};
export function Sidebar({
  activePage,
  onNavigate,
  isMinimized,
  onToggleMinimize
}: SidebarProps) {
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const menuItems = [{
    id: 'home',
    label: 'Home',
    icon: Home,
    locked: false
  }, {
    id: 'stories',
    label: 'Stories',
    icon: BookOpen,
    locked: false,
    tourId: 'sidebar-stories'
  }, {
    id: 'create',
    label: 'Create',
    icon: Wand2,
    locked: false,
    tourId: 'sidebar-create'
  }, {
    id: 'creations',
    label: 'Creations',
    icon: Sparkles,
    locked: false,
    tourId: 'sidebar-creations'
  }, {
    id: 'design-tester',
    label: 'Design Tester',
    icon: Palette,
    locked: false,
    badge: 'TEST'
  }, {
    id: 'prompts',
    label: 'Prompts',
    icon: MessageSquare,
    locked: true
  }, {
    id: 'family',
    label: 'Family',
    icon: Users,
    locked: true
  }, {
    id: 'reminisce',
    label: 'Reminisce',
    icon: Clock,
    locked: true
  }, {
    id: 'labs',
    label: 'Labs',
    icon: FlaskConical,
    locked: true
  }];
  const handleMenuClick = (item: (typeof menuItems)[0]) => {
    if (item.locked) {
      return;
    }
    onNavigate(item.id);
  };
  return <aside className={`bg-white border-r border-gray-200 flex flex-col fixed left-0 top-0 h-screen transition-all duration-300 ${isMinimized ? 'w-20' : 'w-64'}`} data-id="element-1697">
      {/* Logo */}
      <div className="p-6 border-b border-gray-200 flex items-center justify-between min-h-[88px]" data-id="element-1698">
        <div className={`transition-opacity duration-300 ${isMinimized ? 'opacity-0 w-0' : 'opacity-100'}`} data-id="element-1699">
          {!isMinimized && <button onClick={() => onNavigate('home')} className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-id="element-1700">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vPWEA5aXc2AX6PbtUoaz8G/Folklore-logo.png" alt="Folklore" className="h-8" data-id="element-1701" />
            </button>}
        </div>

        <button onClick={onToggleMinimize} className={`p-2 hover:bg-gray-100 rounded-lg transition-all flex-shrink-0 ${isMinimized ? 'mx-auto' : 'ml-auto'}`} title={isMinimized ? 'Expand sidebar' : 'Minimize sidebar'} data-id="element-1702">
          {isMinimized ? <ChevronRight className="w-5 h-5 text-gray-600" data-id="element-1703" /> : <ChevronLeft className="w-5 h-5 text-gray-600" data-id="element-1704" />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto" data-id="element-1705">
        <ul className="space-y-1" data-id="element-1706">
          {menuItems.map(item => {
          const Icon = item.icon;
          const isActive = activePage === item.id;
          return <li key={item.id} data-id="element-1707">
                <button onClick={() => handleMenuClick(item)} disabled={item.locked} data-tour={item.tourId} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all relative ${item.locked ? 'opacity-50 cursor-not-allowed' : isActive ? 'bg-green-50 text-green-600 shadow-sm' : 'text-gray-700 hover:bg-gray-50'}`} title={isMinimized ? item.label : undefined} data-id="element-1708">
                  <Icon className="w-5 h-5 flex-shrink-0" data-id="element-1709" />
                  <span className={`font-medium whitespace-nowrap transition-all duration-300 ${isMinimized ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`} data-id="element-1710">
                    {item.label}
                  </span>
                  {item.badge && !isMinimized && <span className="ml-auto px-2 py-0.5 bg-orange-100 text-orange-600 text-xs font-bold rounded" data-id="element-1711">
                      {item.badge}
                    </span>}
                  {item.locked && !isMinimized && <Lock className="w-4 h-4 ml-auto flex-shrink-0 text-gray-400" data-id="element-1712" />}
                  {item.locked && isMinimized && <Lock className="w-3 h-3 absolute top-2 right-2 text-gray-400" data-id="element-1713" />}
                </button>
              </li>;
        })}
        </ul>
      </nav>

      {/* Storage Widget */}
      {!isMinimized && <div className="transition-opacity duration-300" data-id="element-1714">
          <StorageWidget storageUsed={0.64} storageTotal={5} creditsRemaining={35} data-id="element-1715" />
        </div>}

      {/* User Profile */}
      <div className="p-4 border-t border-gray-200 relative" data-id="element-1716">
        <button ref={profileButtonRef} onClick={() => setShowProfileDropdown(!showProfileDropdown)} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors" title={isMinimized ? 'Daniel' : undefined} data-id="element-1717">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-1718">
            <span className="text-green-600 font-semibold text-sm" data-id="element-1719">D</span>
          </div>
          <span className={`font-medium text-gray-700 whitespace-nowrap transition-all duration-300 ${isMinimized ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'}`} data-id="element-1720">
            Daniel
          </span>
        </button>

        <ProfileDropdown isOpen={showProfileDropdown} onClose={() => setShowProfileDropdown(false)} onNavigate={onNavigate} anchorRef={profileButtonRef} data-id="element-1721" />
      </div>
    </aside>;
}